import { Autocomplete, Fade, FormControl, TextField } from "@mui/material";
import { useAppSelector } from "../../../store/hooks";
import { selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { selectClusterServer } from "../../../store/serverSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SES } from "../../../constants/accessRights";
import { colorPrimary } from "../../../constants/colors";
import { ServiceTypeModelRobot } from "../../../types/cloudTypes";
import { RequestStatus } from "../../../types/statusTypes";
import Models from "../Models/Models";
import FormAddingModelConfig from "../../Forms/FormAddingModelConfig/FormAddingModelConfig";
import ProgressCircle from "../../MUI/ProgressCircle/ProgressCircle";
import { IModelsTabProps } from "./ModelsTab.props";
import styles from "./ModelsTab.module.scss";
import { Hint } from "@/HOC";

const ModelsTab = ({
	robotData,
	changeFlgHandler,
	inputVoice,
	setInputVoice,
	inputMainRecognitionModel,
	setInputMainRecognitionModel,
	inputBackgroundRecognitionModel,
	setInputBackgroundRecognitionModel,
}: IModelsTabProps): JSX.Element => {
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const { smc: clusterServerSmc, see: clusterServerSee, spr: clusterServerSpr, tts: clusterServerTts } = useAppSelector(selectClusterServer); // store - список моделей на сервере cluster

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.models}>
				<div className={styles.block}>
					{/* голос */}
					<FormControl fullWidth>
						<Hint hint={translate("hint_modelVoice")}>
							<Autocomplete
								freeSolo
								options={clusterServerTts.modelList}
								value={inputVoice}
								onChange={(_, value) => {
									setInputVoice(value ? value : "");
									changeFlgHandler("voice"); // ставим флаг о несохраненных данных
								}}
								disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
								renderInput={(params) => (
									<TextField
										{...params}
										required
										label={translate("input_voice")}
										onChange={(e) => {
											setInputVoice(e.target.value);
											changeFlgHandler("voice"); // ставим флаг о несохраненных данных
										}}
										error={!inputVoice}
										InputLabelProps={{
											style: {
												fontSize: 13,
											},
										}}
										InputProps={{
											...params.InputProps, // важно прокинуть параметры
											endAdornment: (
												<div>
													{clusterServerTts.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
													{params.InputProps.endAdornment} {/* важно дописать параметры */}
												</div>
											),
										}}
										sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
									/>
								)}
								sx={{
									".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
									".MuiInputBase-input": { marginTop: -1 },
								}}
								getOptionLabel={(option) => option}
								renderOption={(props, option) => {
									return (
										<span {...props} style={{ fontSize: 13, color: colorPrimary }}>
											{option}
										</span>
									);
								}}
							/>
						</Hint>
					</FormControl>
					{/* основная модель распознавания */}
					<FormControl fullWidth>
						<Hint hint={translate("hint_basicRecognitionModel")}>
							<Autocomplete
								freeSolo
								options={clusterServerSpr.modelList}
								value={inputMainRecognitionModel}
								onChange={(_, value) => {
									setInputMainRecognitionModel(value ? value : "");
									changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
								}}
								disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
								renderInput={(params) => (
									<TextField
										{...params}
										required
										label={translate("input_mainRecognitionModel")}
										onChange={(e) => {
											setInputMainRecognitionModel(e.target.value);
											changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
										}}
										error={!inputMainRecognitionModel}
										InputLabelProps={{
											style: {
												fontSize: 13,
											},
										}}
										InputProps={{
											...params.InputProps, // важно прокинуть параметры
											endAdornment: (
												<div style={{ marginTop: "-7px" }}>
													{clusterServerSpr.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
													{params.InputProps.endAdornment} {/* важно дописать параметры */}
												</div>
											),
										}}
										sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
									/>
								)}
								sx={{
									".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
									".MuiInputBase-input": { marginTop: -1 },
								}}
								getOptionLabel={(option) => option}
								renderOption={(props, option) => {
									return (
										<span {...props} style={{ fontSize: 13, color: colorPrimary }}>
											{option}
										</span>
									);
								}}
							/>
						</Hint>
					</FormControl>
					{/* фоновая модель распознавания */}
					<FormControl fullWidth>
						<Hint hint={translate("hint_backgroundRecognitionModel")}>
							<Autocomplete
								freeSolo
								options={clusterServerSpr.modelList}
								value={inputBackgroundRecognitionModel}
								onChange={(_, value) => {
									setInputBackgroundRecognitionModel(value ? value : "");
									changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
								}}
								disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
								renderInput={(params) => (
									<TextField
										{...params}
										required
										label={translate("input_backgroundRecognitionModel")}
										onChange={(e) => {
											setInputBackgroundRecognitionModel(e.target.value);
											changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
										}}
										error={!inputBackgroundRecognitionModel}
										InputLabelProps={{
											style: {
												fontSize: 13,
											},
										}}
										InputProps={{
											...params.InputProps, // важно прокинуть параметры
											endAdornment: (
												<div style={{ marginTop: "-7px" }}>
													{clusterServerSpr.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
													{params.InputProps.endAdornment} {/* важно дописать параметры */}
												</div>
											),
										}}
										sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
									/>
								)}
								sx={{
									".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
									".MuiInputBase-input": { marginTop: -1 },
								}}
								getOptionLabel={(option) => option}
								renderOption={(props, option) => {
									return (
										<span {...props} style={{ fontSize: 13, color: colorPrimary }}>
											{option}
										</span>
									);
								}}
							/>
						</Hint>
					</FormControl>
				</div>

				<div className={styles.modelSettings}>
					{Object.keys(robotData.models).map((serviceType) => {
						const serviceTypeModel = serviceType as ServiceTypeModelRobot;
						return Object.keys(robotData.models[serviceTypeModel]).map((model) => {
							return (
								<Models
									key={model}
									serviceType={serviceTypeModel}
									modelName={model}
									configData={robotData.models[serviceTypeModel][model]}
									modelsList={
										serviceTypeModel === "smc" ? clusterServerSmc.modelList : serviceTypeModel === "see" ? clusterServerSee.modelList : clusterServerTts.modelList
									}
									changeFlgHandler={changeFlgHandler}
								/>
							);
						});
					})}

					{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
						<FormAddingModelConfig
							changeFlgHandler={changeFlgHandler}
							modelsListSmc={clusterServerSmc.modelList}
							modelsListSee={clusterServerSee.modelList}
							modelsListTts={clusterServerTts.modelList}
						/>
					)}
				</div>
			</div>
		</Fade>
	);
};

export default ModelsTab;
